import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, MapPin, Phone, Mail, Clock, Hash, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

const Row = ({ icon: Icon, label, value, testId }) => value ? (
  <div className="flex gap-4 border-b border-white/10 py-4" data-testid={testId}>
    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-neutral-400" />
    <div><div className="font-mono-label text-[10px] uppercase tracking-widest text-neutral-500">{label}</div><div className="mt-1 text-neutral-200">{value}</div></div>
  </div>
) : null;

export default function CompanyPage() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/company").then((r) => setC(r.data)).catch(() => {}); }, []);
  if (!c) return <div className="px-4 py-32 text-center text-neutral-500">Loading…</div>;
  const address = [c.address, `${c.city}, ${c.state} ${c.zip}`.trim()].filter(Boolean).join(", ");
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Organization", name: c.legal_name, alternateName: c.dba, url: c.website, description: c.description,
    telephone: c.phone || undefined, email: c.email || undefined, foundingDate: c.founded || undefined, duns: c.duns || undefined, naics: c.naics.split(" ")[0],
    address: { "@type": "PostalAddress", streetAddress: c.address || undefined, addressLocality: c.city, addressRegion: c.state, postalCode: c.zip || undefined, addressCountry: "US" },
    sameAs: [c.instagram, c.facebook, c.linkedin].filter(Boolean),
  };
  return (
    <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-20" data-testid="company-page">
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <span className="eyebrow">Company</span>
      <h1 className="mt-2 font-display text-4xl uppercase text-chrome sm:text-5xl lg:text-6xl" data-testid="company-legal-name">{c.legal_name}</h1>
      <p className="mt-4 max-w-3xl text-neutral-300">{c.description}</p>
      <div className="mt-12 grid gap-10 md:grid-cols-5">
        <div className="md:col-span-3">
          <h2 className="font-display text-2xl uppercase text-white">What we do</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[["Trail Rides & Rodeo", "/request?lane=trail-rides"], ["Concerts & Festivals", "/request?lane=concerts"], ["Corporate & Private Events", "/request?lane=corporate"],
              ["Sponsorship Programs", "/sponsorships"], ["Vendor Marketplaces", "/vendors"], ["Artist Booking", "/request?lane=talent"], ["Management Playbooks", "/products"], ["Consulting Engagements", "/products"]].map(([l, to]) => (
              <Link key={l} to={to} className="glass rounded-2xl p-4 font-cond text-lg uppercase tracking-wide text-neutral-200 transition-colors hover:text-white">{l}</Link>
            ))}
          </div>
          <h2 className="mt-12 font-display text-2xl uppercase text-white">Leadership</h2>
          <p className="mt-3 text-neutral-300">{c.officers || "Owner-operated. Leadership details available on request."}</p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="company-contact-btn" className="btn-gold px-7 py-3.5 text-[12px]">Contact Us</Link>
            <Link to="/request" className="btn-chrome px-7 py-3.5 text-[12px]">Start a Request</Link>
          </div>
        </div>
        <aside className="glass rounded-3xl p-6 md:col-span-2" data-testid="company-facts">
          <h3 className="font-display text-xl uppercase text-white">Business Profile</h3>
          <Row icon={Building2} label="Trade name" value={c.dba} testId="company-dba" />
          <Row icon={ShieldCheck} label="Entity" value={`${c.entity_type} · ${c.state_of_formation}${c.founded ? ` · est. ${c.founded}` : ""}`} />
          <Row icon={Hash} label="NAICS" value={c.naics} />
          <Row icon={Hash} label="SIC" value={c.sic} />
          <Row icon={MapPin} label="Address" value={address} testId="company-address" />
          <Row icon={Phone} label="Phone" value={c.phone} testId="company-phone" />
          <Row icon={Mail} label="Email" value={c.email} testId="company-email" />
          <Row icon={Clock} label="Hours" value={c.hours} />
        </aside>
      </div>
    </div>
  );
}
